import { useCallback, useEffect, useState } from "react";

export type Theme = "dark" | "light";

export const THEME_KEY = "theme";

/* Mirrors the bootstrap script in public/index.html — keep the two in sync. */
export function readStoredTheme(): Theme | null {
  try {
    const saved = localStorage.getItem(THEME_KEY);
    return saved === "dark" || saved === "light" ? saved : null;
  } catch {
    // private mode / storage disabled
    return null;
  }
}

function systemTheme(): Theme {
  return typeof matchMedia === "function" &&
    matchMedia("(prefers-color-scheme: light)").matches
    ? "light"
    : "dark";
}

export function useTheme() {
  // The bootstrap script already set data-theme before React mounted, so read it
  // back rather than recomputing — that keeps the first render flash-free.
  const [theme, setTheme] = useState<Theme>(() => {
    const attr = document.documentElement.dataset.theme;
    if (attr === "dark" || attr === "light") return attr;
    return readStoredTheme() ?? systemTheme();
  });

  useEffect(() => {
    document.documentElement.dataset.theme = theme;

    // keep the mobile browser chrome in step with the page
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute("content", theme === "light" ? "#fbfbfd" : "#0e0e12");
  }, [theme]);

  // Follow the OS, but only until the visitor picks a side themselves. Nothing is
  // written to storage on mount, so an absent key genuinely means "no preference".
  useEffect(() => {
    if (typeof matchMedia !== "function") return;
    const mq = matchMedia("(prefers-color-scheme: light)");
    const onChange = (e: MediaQueryListEvent) => {
      if (readStoredTheme() === null) setTheme(e.matches ? "light" : "dark");
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const toggle = useCallback(() => {
    setTheme((prev) => {
      const next: Theme = prev === "dark" ? "light" : "dark";
      try {
        localStorage.setItem(THEME_KEY, next);
      } catch {
        // ignore — the toggle still works for this session
      }
      return next;
    });
  }, []);

  return { theme, toggle };
}
